import { useEffect } from "react";
import { Truck, Building2, Handshake } from "lucide-react";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import EnquiryForm from "@/components/EnquiryForm";
import { waLink, WhatsAppIcon } from "@/lib/whatsapp";

export default function Bulk() {
  useEffect(() => {
    document.title = "Bulk & Fleet Purchase — AutoWorld";
  }, []);

  return (
    <div className="pt-16 md:pt-20" data-testid="bulk-page">
      <section className="border-b border-black/10">
        <div className="mx-auto max-w-[1400px] px-5 py-14 md:px-10 md:py-20">
          <Reveal>
            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-neutral-500">Fleet · Corporate · Group Buyers</div>
            <h1 className="mt-4 font-display text-5xl font-extrabold uppercase tracking-tighter md:text-7xl">
              Buy in Bulk.<br /><span className="text-stroke">Save Big.</span>
            </h1>
            <p className="mt-4 max-w-lg text-neutral-600">
              Companies, rental fleets and group buyers get volume pricing across Honda, Proton,
              Jaecoo &amp; Omoda — with door-to-door delivery, anywhere in Malaysia.
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee items={["Volume Discounts", "Door-to-Door Delivery", "Corporate Financing", "Honda · Proton · Jaecoo & Omoda"]} />

      <section className="mx-auto max-w-[1400px] px-5 py-16 md:px-10 md:py-24">
        <Reveal className="border border-black p-10 md:p-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-neutral-500">Simple Volume Discount</div>
          <h2 className="mt-4 max-w-3xl font-display text-3xl font-extrabold uppercase tracking-tighter md:text-5xl">
            More than 2 units? You get a discount.
          </h2>
          <p className="mt-4 max-w-xl text-neutral-600 md:text-lg">
            The more units you buy, the bigger the discount. Tell us how many you need — we'll quote you our best fleet price.
          </p>
        </Reveal>

        <div className="mt-16 grid gap-px border border-black/10 bg-black/10 md:grid-cols-3">
          {[
            { icon: Building2, t: "Corporate Financing", d: "Fleet financing structures and documentation handled end-to-end by our team." },
            { icon: Truck, t: "Door-to-Door Delivery", d: "Every unit delivered to your premises, anywhere in Malaysia. You never visit a showroom." },
            { icon: Handshake, t: "Full Aftersales Included", d: "Bulk doesn't mean basic — full service, parts and warranty support included." },
          ].map((f, i) => (
            <Reveal key={f.t} delay={i * 0.08} className="bg-white p-8 md:p-10">
              <f.icon className="h-8 w-8" strokeWidth={1.5} />
              <h3 className="mt-4 font-display text-xl font-bold uppercase tracking-tight">{f.t}</h3>
              <p className="mt-2 text-sm text-neutral-600">{f.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-black text-white">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-5 py-16 md:grid-cols-2 md:px-10 md:py-28">
          <Reveal>
            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-neutral-400">Fleet Enquiry</div>
            <h2 className="mt-4 font-display text-4xl font-extrabold uppercase tracking-tighter md:text-6xl">
              Get a fleet<br />quote
            </h2>
            <p className="mt-6 max-w-md text-neutral-400">
              Tell us what you need — we reply with volume pricing within one working day.
            </p>
            <div className="mt-8 space-y-4 border-t border-white/15 pt-8">
              {[
                ["01", "Enquire — tell us units & models"],
                ["02", "Quote — volume pricing in 1 working day"],
                ["03", "Delivered — door-to-door, nationwide"],
              ].map(([n, s]) => (
                <div key={n} className="flex items-center gap-4">
                  <span className="font-gothic text-3xl text-white/20">{n}</span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-300">{s}</span>
                </div>
              ))}
            </div>
            <a
              href={waLink("Hi AutoWorld! I'd like a bulk/fleet purchase quote.")}
              target="_blank"
              rel="noreferrer"
              data-testid="bulk-whatsapp-cta"
              className="mt-8 inline-flex items-center gap-2 border border-white px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors hover:bg-white hover:text-black"
            >
              <WhatsAppIcon className="h-4 w-4" /> Or WhatsApp us directly
            </a>
          </Reveal>
          <Reveal delay={0.15}>
            <EnquiryForm
              type="bulk"
              dark
              testid="bulk-enquiry"
              submitLabel="Request Fleet Quote"
              extra={[
                { name: "company", label: "Company / Organisation", placeholder: "Sdn. Bhd." },
                { name: "units", label: "Estimated Units", placeholder: "e.g. 5" },
              ]}
            />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
